import React, { useEffect, useRef } from 'react';

const ALGO_SYMBOLS = [
  'EPA','∑','∫','λ','∂','∇','σ','μ','O(n)','n²','ΔE','β','α','≈','→','∞','π','ρ','NR','≥','∈','//','{}','YAC','%',
];

const FOOTBALL = '🏈';
const FONT_SIZE = 22;
const FLAG_SIZE = 11;

// Draw a penalty flag: large ball leads, 3-lobe fabric trails behind
function drawPenaltyFlag(ctx, x, y, angle, size) {
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(angle);

  const r  = size * 0.7;   // ball radius (prominent)
  const nx = -r * 0.7;     // neck x (gather point behind ball)
  const L  = size * 2.8;   // fabric reach
  const BASE_ALPHA = ctx.globalAlpha;

  // Helper — draw one fabric lobe
  function lobe(cp1x, cp1y, cp2x, cp2y, ex, ey,   // out bezier
                cp3x, cp3y, cp4x, cp4y,            // return bezier back to neck
                shade) {
    ctx.beginPath();
    ctx.moveTo(nx, -r * 0.2);
    ctx.bezierCurveTo(cp1x, cp1y, cp2x, cp2y, ex, ey);
    ctx.bezierCurveTo(cp3x, cp3y, cp4x, cp4y, nx, r * 0.2);
    ctx.closePath();
    ctx.fillStyle = shade;
    ctx.fill();
  }

  // Upper lobe — fans up-right
  lobe(
    nx - L * 0.15, -r * 1.1,   nx - L * 0.5,  -r * 1.5,
    -L,            -r * 0.6,
    -L * 0.55,     -r * 0.1,   nx - L * 0.1,   r * 0.05,
    '#f5c200'
  );

  // Middle lobe — goes straight right (largest)
  lobe(
    nx - L * 0.2,  -r * 0.3,   nx - L * 0.7,  -r * 0.1,
    -L * 1.05,      r * 0.5,
    -L * 0.6,       r * 0.8,   nx - L * 0.1,   r * 0.4,
    '#f0b800'
  );

  // Lower lobe — drops down-left
  lobe(
    nx - L * 0.1,  r * 0.5,    nx - L * 0.35,  r * 1.1,
    -L * 0.5,       r * 1.6,
    -L * 0.3,       r * 1.2,   nx,              r * 0.5,
    '#e8a800'
  );

  // Crease lines for depth
  ctx.strokeStyle = '#c89000';
  ctx.lineWidth = 0.9;
  ctx.globalAlpha = BASE_ALPHA * 0.45;
  // Upper crease
  ctx.beginPath();
  ctx.moveTo(nx, -r * 0.1);
  ctx.bezierCurveTo(nx - L * 0.3, -r * 0.6, nx - L * 0.65, -r * 0.5, -L * 0.9, -r * 0.4);
  ctx.stroke();
  // Lower crease
  ctx.beginPath();
  ctx.moveTo(nx, r * 0.25);
  ctx.bezierCurveTo(nx - L * 0.3, r * 0.55, nx - L * 0.55, r * 0.7, -L * 0.75, r * 1.0);
  ctx.stroke();
  ctx.globalAlpha = BASE_ALPHA;

  // Gathered neck wrinkle (fabric bunches at ball)
  ctx.strokeStyle = '#c89000';
  ctx.lineWidth = 1.2;
  ctx.beginPath();
  ctx.arc(nx, 0, r * 0.22, -Math.PI * 0.6, Math.PI * 0.6);
  ctx.stroke();

  // ── Weighted ball ──
  const grad = ctx.createRadialGradient(
    -r * 0.3, -r * 0.3, r * 0.05,
     0,        0,        r
  );
  grad.addColorStop(0, '#ffe566');
  grad.addColorStop(0.55, '#f5c200');
  grad.addColorStop(1, '#b8880a');
  ctx.fillStyle = grad;
  ctx.beginPath();
  ctx.arc(0, 0, r, 0, Math.PI * 2);
  ctx.fill();

  // Fabric texture on ball (subtle lines)
  ctx.strokeStyle = '#d4a000';
  ctx.lineWidth = 0.6;
  ctx.globalAlpha = BASE_ALPHA * 0.3;
  for (let i = -1; i <= 1; i++) {
    ctx.beginPath();
    ctx.arc(0, 0, r * 0.65, Math.PI * (0.15 + i * 0.22), Math.PI * (0.85 - i * 0.22));
    ctx.stroke();
  }
  ctx.globalAlpha = BASE_ALPHA;

  // Ball highlight
  ctx.fillStyle = 'rgba(255,255,210,0.3)';
  ctx.beginPath();
  ctx.arc(-r * 0.3, -r * 0.3, r * 0.32, 0, Math.PI * 2);
  ctx.fill();

  ctx.restore();
}
// const TRAIL_LEN = 14;       // number of trail symbols per football
const SPAWN_INTERVAL_MIN = 6000;  // ms between spawns (min)
const SPAWN_INTERVAL_MAX = 12000; // ms between spawns (max)

function randomBetween(a, b) { return a + Math.random() * (b - a); }

// A single dropped symbol that stays in place and fades out
class DroppedSymbol {
  constructor(x, y, symbol) {
    this.x = x;
    this.y = y;
    this.symbol = symbol;
    this.alpha = 0.75;
    this.initialAlpha = 0.75;
    this.fadeRate = randomBetween(0.008, 0.018);
    this.initialFontSize = Math.floor(randomBetween(9, 14));
    this.done = false;
  }
  tick() {
    this.alpha -= this.fadeRate;
    if (this.alpha <= 0) { this.alpha = 0; this.done = true; }
  }
  draw(ctx) {
    // Grow from initial size to 2x as alpha fades to 0
    const progress = 1 - (this.alpha / this.initialAlpha); // 0 (fresh) → 1 (gone)
    const fontSize = this.initialFontSize * (1 + progress);
    ctx.save();
    ctx.globalAlpha = this.alpha;
    ctx.font = `${fontSize.toFixed(1)}px monospace`;
    ctx.fillStyle = '#b0bec5';
    // Offset so symbol expands from its center, not top-left
    ctx.textAlign = 'center';
    ctx.fillText(this.symbol, this.x, this.y);
    ctx.restore();
  }
}

class FootballPass {
  constructor(canvasW, canvasH) {
    this.canvasW = canvasW;
    this.canvasH = canvasH;
    this.reset(canvasW, canvasH);
  }

  reset(canvasW, canvasH) {
    this.canvasW = canvasW;
    this.canvasH = canvasH;

    this.leftToRight = Math.random() > 0.5;

    const startY = randomBetween(canvasH * 0.45, canvasH * 0.75);
    const endY   = randomBetween(canvasH * 0.45, canvasH * 0.75);
    const peakY  = randomBetween(canvasH * 0.12, canvasH * 0.38);

    this.startX  = this.leftToRight ? -FONT_SIZE * 2 : canvasW + FONT_SIZE * 2;
    this.endX    = this.leftToRight ? canvasW + FONT_SIZE * 2 : -FONT_SIZE * 2;
    this.startY  = startY;
    this.endY    = endY;
    this.peakY   = peakY;
    this.peakX   = canvasW / 2 + randomBetween(-canvasW * 0.15, canvasW * 0.15);

    // ~1 in 5 chance of being a penalty flag instead of a football
    this.isPenaltyFlag = Math.random() < 0.35;

    if (this.isPenaltyFlag) {
      // Thrown straight-ish up from bottom, peaks ~30-40% from top, falls back down
      const throwX   = randomBetween(canvasW * 0.15, canvasW * 0.85);
      const landX    = throwX + randomBetween(-canvasW * 0.28, canvasW * 0.28);
      this.startX    = throwX;
      this.startY    = canvasH + 20;
      this.endX      = landX;
      this.endY      = canvasH + 20;
      this.peakX     = (throwX + landX) / 2 + randomBetween(-canvasW * 0.05, canvasW * 0.05);
      // control point must be above actual desired peak due to bezier math:
      // actual_peak = 0.5 * startY + 0.5 * controlY → controlY = 2*target - startY
      // target = canvasH * 0.45–0.55 from top → controlY ≈ -0.1*canvasH to +0.1*canvasH
      const targetPeak = randomBetween(canvasH * 0.45, canvasH * 0.55);
      this.peakY = 2 * targetPeak - (canvasH + 20);
      this.totalFrames = Math.floor(randomBetween(70, 110)); // slightly faster arc
    }

    this.totalFrames = this.totalFrames || Math.floor(randomBetween(90, 160));
    this.frame = 0;

    // Drop a symbol every N frames
    this.dropEvery = 4;

    this.done = false;
  }

  getPos(t) {
    const mt = 1 - t;
    const x = mt * mt * this.startX + 2 * mt * t * this.peakX + t * t * this.endX;
    const y = mt * mt * this.startY + 2 * mt * t * this.peakY + t * t * this.endY;
    return { x, y };
  }

  getAngle(t) {
    const dt = 0.01;
    const t2 = Math.min(t + dt, 1);
    const p1 = this.getPos(t);
    const p2 = this.getPos(t2);
    return Math.atan2(p2.y - p1.y, p2.x - p1.x);
  }

  // Returns a new DroppedSymbol at current position, or null
  tick() {
    this.frame++;
    if (this.frame >= this.totalFrames) { this.done = true; return null; }

    if (this.frame % this.dropEvery === 0) {
      const t = this.frame / this.totalFrames;
      const pos = this.getPos(t);
      const symbol = ALGO_SYMBOLS[Math.floor(Math.random() * ALGO_SYMBOLS.length)];
      // Spawn from football center with tiny random scatter
      const offsetX = randomBetween(-4, 4);
      const offsetY = randomBetween(-4, 4);
      return new DroppedSymbol(pos.x + offsetX, pos.y + offsetY, symbol);
    }
    return null;
  }

  draw(ctx) {
    if (this.frame >= this.totalFrames) return;
    const t = this.frame / this.totalFrames;
    const pos = this.getPos(t);
    const angle = this.getAngle(t);

    ctx.globalAlpha = 0.88;
    if (this.isPenaltyFlag) {
      drawPenaltyFlag(ctx, pos.x, pos.y, angle, FLAG_SIZE);
    } else {
      const fs = FONT_SIZE * 1.4;
      ctx.save();
      ctx.translate(pos.x, pos.y);
      ctx.rotate(angle);
      ctx.font = `${fs}px serif`;
      ctx.fillText(FOOTBALL, -fs / 2, fs / 2);
      ctx.restore();
    }
    ctx.globalAlpha = 1;
  }
}

export default function FootballPassCanvas() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    const resize = () => {
      canvas.width  = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    const passes  = [];
    const symbols = []; // dropped symbols staying in place
    let animFrame;
    let spawnTimer;

    const spawnPass = () => {
      passes.push(new FootballPass(canvas.width, canvas.height));
      const delay = randomBetween(SPAWN_INTERVAL_MIN, SPAWN_INTERVAL_MAX);
      spawnTimer = setTimeout(spawnPass, delay);
    };

    spawnTimer = setTimeout(spawnPass, randomBetween(1500, 4000));

    const handleVisibility = () => {
      if (document.hidden) {
        clearTimeout(spawnTimer);
        cancelAnimationFrame(animFrame);
      } else {
        // Clear any built-up passes when returning to tab
        passes.length = 0;
        symbols.length = 0;
        last = 0;
        animFrame = requestAnimationFrame(draw);
        spawnTimer = setTimeout(spawnPass, randomBetween(800, 2000));
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);

    let last = 0;
    const draw = (ts) => {
      animFrame = requestAnimationFrame(draw);
      if (ts - last < 33) return;
      last = ts;

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Tick footballs — collect any new dropped symbols
      for (let i = passes.length - 1; i >= 0; i--) {
        const dropped = passes[i].tick();
        if (dropped) symbols.push(dropped);
        passes[i].draw(ctx);
        if (passes[i].done) passes.splice(i, 1);
      }

      // Tick & draw lingering symbols
      for (let i = symbols.length - 1; i >= 0; i--) {
        symbols[i].tick();
        symbols[i].draw(ctx);
        if (symbols[i].done) symbols.splice(i, 1);
      }
    };

    animFrame = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(animFrame);
      clearTimeout(spawnTimer);
      window.removeEventListener('resize', resize);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        zIndex: 1,
        pointerEvents: 'none',
        display: 'block',
      }}
    />
  );
}
